<template>
  <v-tour :name="tourName" :steps="tourSteps" :callbacks="tourCallbacks">
    <template slot-scope="tour">
      <transition name="fade">
        <v-step
          v-if="tour.steps[tour.currentStep]"
          :step="tour.steps[tour.currentStep]"
          :key="tour.currentStep"
          :previous-step="tour.previousStep"
          :next-step="tour.nextStep"
          :stop="tour.stop"
          :skip="tour.skip"
          :finish="tour.finish"
          :is-first="tour.isFirst"
          :is-last="tour.isLast"
          :labels="tour.labels"
        ></v-step>
      </transition>
    </template>
  </v-tour>
</template>
<script>
import eventDispatcher from '../../../events/dispatch';

export default {
  name: 'OnboardingTour',
  props: ['tourName', 'tourSteps', 'beforeRestartHook'],
  data() {
    return {
      tourCallbacks: {
        onStart: this.handleRestart,
        onStop: this.handleStop,
        onSkip: this.handleSkip,
        onFinish: this.handleFinish,
      },
    };
  },
  computed: {
    showTour: {
      get() {
        const seenDashboardTours = this.getViewedDashBoardTours();

        return !seenDashboardTours[this.tourName];
      },
      set(newValue) {
        this.updateViewedDashboardTours(newValue);
      },
    },
  },
  methods: {
    updateViewedDashboardTours(value) {
      const seenDashboardTours = this.getViewedDashBoardTours();

      seenDashboardTours[this.tourName] = !value;

      window.localStorage.setItem('seenDashboardTours', JSON.stringify(seenDashboardTours));
    },
    getViewedDashBoardTours() {
      const seenDashboardTours = window.localStorage.getItem('seenDashboardTours');

      return seenDashboardTours ? JSON.parse(seenDashboardTours) : {};
    },
    createEventObject() {
      const stepIndex = this.$tours[this.tourName].currentStep;
      const step = this.tourSteps[stepIndex];
      const step_title = step && step.header && step.header.title;

      return {
        tour_name: this.tourName,
        step_index: stepIndex,
        step_count: this.tourSteps.length,
        step_title,
      };
    },
    /**
     * `handleRestart` will always be called when the tour starts since
     * it's in the tour plugins start handler.
     * The initial showing of the tour only happens on the mounted hook if `this.showTour` is true.
     * Because of that, we know that if a tour is started and `this.showTour` is false,
     * it is a restart and we should reset local storage
     */
    handleRestart() {
      const eventObject = this.createEventObject();

      if (this.beforeRestartHook) {
        this.beforeRestartHook();
      }

      if (!this.showTour) {
        eventObject.is_restart = true;
        this.showTour = true;
      } else {
        eventObject.is_restart = false;
      }
      eventDispatcher('onboardingStarted', eventObject);
    },
    handleStop() {
      this.showTour = false;
    },
    handleSkip() {
      const eventObject = this.createEventObject();

      eventDispatcher('onboardingSkipped', eventObject);
    },
    handleFinish() {
      const eventObject = this.createEventObject();

      eventDispatcher('onboardingFinished', eventObject);
    },
  },
  mounted() {
    if (this.showTour) {
      this.$tours[this.tourName].start();
    }
  },
};
</script>
<style>
body.v-tour--active {
  pointer-events: auto
}

.v-tour {
  pointer-events: auto;
  font-size: initial;
}

.v-tour__target--highlighted {
  box-shadow: 0 0 0 4px rgba(0, 0, 0, .4);
  pointer-events: auto;
  z-index: 9999
}

.v-tour__target--relative {
  position: relative
}

.v-step {
  background: #1D1D1D;
  color: #fff;
  max-width: 320px;
  border-radius: 20px;
  filter: drop-shadow(0 0 2px rgba(0, 0, 0, .5));
  padding: 1rem;
  text-align: center;
  z-index: 10000
}

.v-step .v-step__arrow {
  width: 0;
  height: 0;
  border-style: solid;
  position: absolute;
  margin: .5rem;
  border-color: #1D1D1D
}

.v-step .v-step__arrow--dark {
  border-color: #1D1D1D
}

.v-step[x-placement^=top] {
  margin-bottom: .5rem
}

.v-step[x-placement^=top] .v-step__arrow {
  border-width: .5rem .5rem 0 .5rem;
  border-left-color: transparent;
  border-right-color: transparent;
  border-bottom-color: transparent;
  bottom: -.5rem;
  left: calc(50% - 1rem);
  margin-top: 0;
  margin-bottom: 0
}

.v-step[x-placement^=bottom] {
  margin-top: .5rem
}

.v-step[x-placement^=bottom] .v-step__arrow {
  border-width: 0 .5rem .5rem .5rem;
  border-left-color: transparent;
  border-right-color: transparent;
  border-top-color: transparent;
  top: -.5rem;
  left: calc(50% - 1rem);
  margin-top: 0;
  margin-bottom: 0
}

.v-step[x-placement^=right] {
  margin-left: .5rem
}

.v-step[x-placement^=right] .v-step__arrow {
  border-width: .5rem .5rem .5rem 0;
  border-left-color: transparent;
  border-top-color: transparent;
  border-bottom-color: transparent;
  left: -.5rem;
  top: calc(50% - 1rem);
  margin-left: 0;
  margin-right: 0
}

.v-step[x-placement^=left] {
  margin-right: .5rem
}

.v-step[x-placement^=left] .v-step__arrow {
  border-width: .5rem 0 .5rem .5rem;
  border-top-color: transparent;
  border-right-color: transparent;
  border-bottom-color: transparent;
  right: -.5rem;
  top: calc(50% - 1rem);
  margin-left: 0;
  margin-right: 0
}

.v-step__header {
  margin: -1rem -1rem .5rem;
  padding: .5rem;
  background-color: #454d5d;
  border-top-left-radius: 20px;
  border-top-right-radius: 20px
}

.v-step__content {
  margin: 0 0 1rem 0
}

.v-step__button {
  background: transparent;
  border: .05rem solid #fff;
  border-radius: .1rem;
  color: #fff;
  cursor: pointer;
  display: inline-block;
  font-size: .8rem;
  height: 1.8rem;
  line-height: 1rem;
  outline: none;
  margin: 0 .2rem;
  padding: .35rem .4rem;
  text-align: center;
  text-decoration: none;
  transition: all .2s ease;
  vertical-align: middle;
  white-space: nowrap
}

.v-step__button:hover {
  background-color: hsla(0, 0%, 100%, .95);
  color: #50596c
}

.fade-enter-active,.fade-leave-active {
  transition: opacity 0.8s
}

.fade-enter,.fade-leave-to {
  opacity: 0
}
</style>
